import { DatalayerDataAttributes, DatalayerLinkTypes } from './App.datalayer.constants';
import { last, pipe, split } from 'lodash/fp';

/**
 * Determines the type of link based on the anchor element's attributes and properties.
 *
 * The function checks for specific data attributes, text content, and CSS classes
 * to categorize the link into different types (icon, CTA, or regular link).
 *
 * @param {HTMLAnchorElement} target - The anchor element to analyze
 * @returns {string} The determined link type:
 *   - Returns the value of DATALAYER_LINK_TYPE or DATALAYER_TYPE attribute if present
 *   - Returns 'icon' if the link has no text content
 *   - Returns 'cta' if the link has MuiButton-root class
 *   - Returns 'link' as default type
 */
export const getLinkType = (target: HTMLAnchorElement): string => {
  const linkTypeAttr = target.getAttribute(DatalayerDataAttributes.DATALAYER_LINK_TYPE);
  const typeAttr = target.getAttribute(DatalayerDataAttributes.DATALAYER_TYPE);

  if (linkTypeAttr || typeAttr) {
    return linkTypeAttr || typeAttr || '';
  }

  if (!target.textContent?.trim()) {
    return DatalayerLinkTypes.ICON;
  }

  if (target.classList.contains('MuiButton-root')) {
    return DatalayerLinkTypes.CTA;
  }

  return DatalayerLinkTypes.LINK;
};

export const getDatalayerAttr =
  (target: HTMLAnchorElement) => (datalayerProp: DatalayerDataAttributes) =>
    target?.getAttribute(datalayerProp);

export const getDatalayerPosition = (target: HTMLAnchorElement) =>
  target?.closest(`[${DatalayerDataAttributes.DATA_DATALAYER_POSITION}]`) as HTMLDivElement;

export const eleIsLinkDownloadType = (target: HTMLAnchorElement) =>
  target?.getAttribute(DatalayerDataAttributes.DATALAYER_LINK_TYPE) === DatalayerLinkTypes.DOWNLOAD;

export const isLinkIgnoredByPosition = ({ target, position }: any) => {
  const positionToIgnore = target?.getAttribute('data-datalayer-ignore-link-position');
  if (positionToIgnore && positionToIgnore === position) return true;
  return false;
};

/**
 * Extracts the numeric tab index from a given string identifier.
 *
 * The identifier is expected to follow the format `"test-id-<number>"`.
 * If the `id` is invalid or empty, an error is thrown.
 *
 * @param {string} id - The string identifier, e.g., "test-id-10".
 * @returns {number} - The numeric tab index extracted from the identifier.
 * @throws {Error} - If the `id` is empty or doesn't contain a valid numeric index.
 */
export const getTabIndex = (id: string): number => {
  if (!id) {
    throw new Error('The parsed index should have a validate valur test-id-10');
  }

  return pipe(
    split('-'),
    last, // getting the last element because it supposed to be the id number,
    (stringId: string | undefined) => parseInt(stringId || '', 10),
  )(id);
};
